import { useEffect, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload as UploadIcon, FileSpreadsheet, CheckCircle2, AlertCircle, Download } from "lucide-react";
import { toast } from "sonner";

export default function Upload() {
  const [file, setFile] = useState(null);
  const [uploadType, setUploadType] = useState("campaigns");
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [history, setHistory] = useState([]);

  const refreshHistory = async () => {
    try {
      const r = await api.get("/uploads/history");
      setHistory(r.data);
    } catch {}
  };
  useEffect(() => { refreshHistory(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview(null);
  };

  const runPreview = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("upload_type", uploadType);
    try {
      const token = localStorage.getItem("bwz_token");
      const r = await fetch(`${API_BASE}/uploads/preview`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(formatApiError({ response: { data: err } }));
      }
      const data = await r.json();
      setPreview(data);
      toast.success(`Preview ready (${data.preview_count} rows from "${data.sheet_used}")`);
    } catch (e) {
      toast.error(e.message || "Preview failed");
    }
  };

  const runCommit = async () => {
    if (!file) return;
    setCommitting(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("upload_type", uploadType);
    try {
      const token = localStorage.getItem("bwz_token");
      const r = await fetch(`${API_BASE}/uploads/commit`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(formatApiError({ response: { data: err } }));
      }
      const data = await r.json();
      toast.success(`Imported ${data.inserted} records (${data.skipped} skipped, ${data.errors.length} errors)`);
      setFile(null);
      setPreview(null);
      refreshHistory();
    } catch (e) {
      toast.error(e.message || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  const downloadSample = async () => {
    try {
      const token = localStorage.getItem("bwz_token");
      const r = await fetch(`${API_BASE}/uploads/sample/${uploadType}.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Could not generate sample");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bwz-ops-sample-${uploadType}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Sample download failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="upload-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Bulk Import</p>
        <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Upload Center</h2>
        <p className="text-sm text-zinc-500 mt-1">Migrate from the legacy Excel — only the <span className="font-mono bg-zinc-100 px-1 rounded">Overview</span> sheet is processed. Download the sample template to see the expected column headers.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-5" data-testid="upload-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs uppercase tracking-wider text-zinc-600">Upload type</Label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger className="mt-1.5" data-testid="upload-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="campaigns">Campaigns master</SelectItem>
                  <SelectItem value="executions">Daily executions</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadSample}
              data-testid="upload-sample-button"
              className="self-end"
            >
              <Download className="h-4 w-4 mr-1.5" /> Download sample
            </Button>
          </div>

          <label
            htmlFor="upload-file-input"
            className="mt-4 border-2 border-dashed border-zinc-300 hover:border-[#0055FF] bg-zinc-50 hover:bg-[#0055FF]/5 transition-colors duration-200 rounded-lg p-10 flex flex-col items-center justify-center text-center cursor-pointer"
            data-testid="upload-dropzone"
          >
            <UploadIcon className="h-8 w-8 text-zinc-400 mb-3" />
            <div className="font-medium text-sm">{file ? file.name : "Click to choose Excel (.xlsx)"}</div>
            <div className="text-xs text-zinc-500 mt-1">Only the “Overview” sheet will be parsed</div>
            <input id="upload-file-input" type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="upload-file-input" />
          </label>

          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={runPreview} disabled={!file} data-testid="upload-preview-button">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Preview
            </Button>
            <Button onClick={runCommit} disabled={!file || committing} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="upload-commit-button">
              <CheckCircle2 className="h-4 w-4 mr-2" /> {committing ? "Importing…" : "Import"}
            </Button>
          </div>
        </Card>

        <Card className="p-5" data-testid="upload-history-card">
          <h3 className="font-display text-lg font-semibold mb-3">Import history</h3>
          {history.length === 0 && <p className="text-sm text-zinc-500">No imports yet.</p>}
          <div className="space-y-2 max-h-96 overflow-auto">
            {history.map((h) => (
              <div key={h.id} className="border border-zinc-200 rounded-md p-3 text-sm" data-testid={`history-row-${h.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{h.filename}</span>
                  <span className="text-xs font-mono text-zinc-500">{(h.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="text-xs text-zinc-600 mt-1">
                  {h.upload_type} · sheet <span className="font-mono">{h.sheet}</span> · inserted <span className="font-mono text-emerald-700">{h.inserted}</span>
                  {h.skipped > 0 && <> · skipped <span className="font-mono text-amber-700">{h.skipped}</span></>}
                  {h.errors?.length > 0 && <> · errors <span className="font-mono text-red-700">{h.errors.length}</span></>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {preview && (
        <Card className="p-5 overflow-hidden" data-testid="upload-preview-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display text-lg font-semibold">Preview — {preview.sheet_used}</h3>
              <p className="text-xs text-zinc-500">Showing first {preview.preview_count} rows</p>
            </div>
            {preview.all_sheets.length > 1 && (
              <span className="text-xs text-amber-700 inline-flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" /> Other sheets ignored: {preview.all_sheets.filter((s) => s !== preview.sheet_used).join(", ")}
              </span>
            )}
          </div>
          <div className="overflow-x-auto border border-zinc-200 rounded-md max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 bg-zinc-50 border-b sticky top-0">
                <tr>
                  {preview.headers.map((h, i) => <th key={i} className="text-left py-2 px-3 font-semibold whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {preview.preview_rows.map((row, ri) => (
                  <tr key={ri} className="border-b border-zinc-100">
                    {row.map((cell, ci) => <td key={ci} className="py-1.5 px-3 whitespace-nowrap">{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
